import React, { useState } from "react";
import {
  FiCalendar,
  FiPlus,
  FiSave,
  FiX,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { BlockedPeriod } from "./BlockedPeriodItem";
import BlockedPeriodItem from "./BlockedPeriodItem";

interface BlockedPeriodsSectionProps {
  blockedPeriods: BlockedPeriod[];
  errorsBlocked: Record<number, string>;
  savingBlocked: boolean;
  hasChangesBlocked: boolean;
  onAdd: () => void;
  onChange: (index: number, field: keyof BlockedPeriod, value: string) => void;
  onRemove: (index: number) => void;
  onSave: () => void;
  onCancel: () => void;
  getDurationDays: (startDate: string, endDate: string) => number;
}

export default function BlockedPeriodsSection({
  blockedPeriods,
  errorsBlocked,
  savingBlocked,
  hasChangesBlocked,
  onAdd,
  onChange,
  onRemove,
  onSave,
  onCancel,
  getDurationDays,
}: BlockedPeriodsSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="mt-8">
      <div className="rounded-lg border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark">
        {/* Header - Sempre visível */}
        <div
          className="flex cursor-pointer items-center justify-between p-4 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-orange-100 p-2 dark:bg-orange-900/30">
              <FiCalendar
                className="text-orange-600 dark:text-orange-400"
                size={20}
              />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                Períodos Bloqueados
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Configure períodos em que você não estará disponível (férias,
                dias únicos, etc)
              </p>
            </div>
          </div>
          {isExpanded ? (
            <FiChevronUp className="text-gray-400" size={20} />
          ) : (
            <FiChevronDown className="text-gray-400" size={20} />
          )}
        </div>

        {/* Conteúdo - Visível apenas quando expandido */}
        {isExpanded && (
          <div className="border-t border-stroke p-6 dark:border-strokedark">
            <div className="space-y-6">
              {/* Botão Adicionar */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAdd();
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                >
                  <FiPlus size={18} />
                  <span>Adicionar Período</span>
                </button>
              </div>

              {/* Lista de períodos bloqueados */}
              {blockedPeriods.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center dark:border-gray-700 dark:bg-gray-800">
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30">
                    <FiCalendar
                      size={32}
                      className="text-orange-600 dark:text-orange-400"
                    />
                  </div>
                  <h4 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
                    Nenhum período bloqueado
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Clique em "Adicionar Período" para bloquear datas específicas
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {blockedPeriods.map((period, index) => (
                    <BlockedPeriodItem
                      key={index}
                      period={period}
                      index={index}
                      error={errorsBlocked[index]}
                      onChange={onChange}
                      onRemove={onRemove}
                      getDurationDays={getDurationDays}
                    />
                  ))}
                </div>
              )}

              {/* Botões de ação para períodos bloqueados */}
              {blockedPeriods.length > 0 && (
                <div className="flex flex-col-reverse gap-3 border-t border-stroke pt-6 sm:flex-row sm:justify-end dark:border-strokedark">
                  {hasChangesBlocked && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCancel();
                        }}
                        disabled={savingBlocked}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-6 py-3 font-medium text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 dark:border-strokedark dark:bg-boxdark dark:text-gray-300 dark:hover:bg-meta-4"
                      >
                        <FiX size={18} />
                        <span>Cancelar</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSave();
                        }}
                        disabled={
                          savingBlocked || Object.keys(errorsBlocked).length > 0
                        }
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-6 py-3 font-medium text-white shadow-sm transition hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {savingBlocked ? (
                          <>
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                            <span>Salvando...</span>
                          </>
                        ) : (
                          <>
                            <FiSave size={18} />
                            <span>Salvar Períodos</span>
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
